import { getLocale } from '../../lib/i18n'

/**
 * Wording for optional residency verification. Kept beside the feature rather than in lib/i18n.ts so
 * the feature can be reviewed, merged or removed as one unit. Every string exists in English and
 * Puerto Rican Spanish. Draft copy: the legal and privacy wording needs the owner's and a lawyer's review.
 */
const EN = {
  title: 'Vieques residency check',
  optional: 'Optional',
  lede: 'Verification is optional. You can browse, buy and sell without it. A verified resident gets a badge and a small, transparent boost in how listings are ordered.',
  notAGuarantee: 'A badge shows what was checked and when. It does not promise that someone is safe, honest or will complete a good exchange.',
  testMode: 'Test mode: no real documents or photos are collected and nothing is sent to a provider.',
  unavailableTitle: 'Not available yet',
  unavailableText: 'Residency checks are not turned on. Nothing is collected until they are.',
  consentTitle: 'Before you start',
  willCheck: 'What will be checked',
  checkIdentity: 'Identity: a specialist provider checks a photo ID and a live selfie. This is separate from residency.',
  checkResidency: 'Residency: evidence that you currently live in Vieques. An ID with a Vieques address helps, or you can use other evidence that a person reviews.',
  provider: 'Who handles your documents',
  providerText: 'A specialist verification provider collects the ID and selfie on its own page. They never pass through our storage.',
  weKeep: 'What we keep',
  weKeepText: 'Only a reference, which checks you did, the result, dates and the version of this consent. We do not keep document images, face data, ID numbers or your address.',
  publicTitle: 'What others can see',
  publicText: 'Only the badge and the date it was checked. Never your legal name, address, ID number, documents or selfie.',
  deletionTitle: 'Withdrawing',
  deletionText: 'You can withdraw at any time. Your badge goes away and we ask the provider to delete what it holds. What a provider must keep by law is theirs to explain and we cannot remove it.',
  agree: 'I understand and agree',
  agreeAndContinue: 'Agree and continue',
  identityTitle: 'Identity check',
  identityText: 'A photo ID and a quick live selfie, done on the provider\'s page.',
  residencyTitle: 'Residency check',
  residencyText: 'Shows that you currently live in Vieques.',
  methodTitle: 'How would you like to show it?',
  methodId: 'My ID shows a Vieques address',
  methodIdHint: 'Needs the identity check first.',
  methodManual: 'My ID address is out of date',
  methodManualHint: 'Share other evidence. A person reviews it.',
  start: 'Start',
  retry: 'Try again',
  respond: 'I have sent more',
  withdraw: 'Withdraw',
  statusLabel: 'Status',
  notStarted: 'Not started',
  pending: 'In progress',
  needs_info: 'Needs more information',
  verified: 'Checked',
  unsuccessful: 'Could not complete',
  expired: 'Expired',
  withdrawn: 'Withdrawn',
  pendingHelp: 'Finish the steps on the provider\'s page. We will update this when it is done.',
  needsInfoHelp: 'We need a little more to finish this check.',
  unsuccessfulHelp: 'We could not complete this check. You can try again, or ask a person to look at it.',
  expiredHelp: 'This check has run out. Start it again to keep your badge.',
  withdrawnHelp: 'You withdrew this check. You can start a new one any time.',
  address_not_vieques: 'The address on your ID is not in Vieques. If you live in Vieques, choose "My ID address is out of date".',
  address_not_current: 'Please share something that shows where you live now.',
  evidence_unclear: 'The evidence was hard to read. Please send it again.',
  more_evidence: 'Please share one more piece of evidence.',
  conflicting_result: 'We got a conflicting result. A person will take a look.',
  checkedOn: 'Checked on',
  validUntil: 'Valid until',
  helpTitle: 'Help and appeal',
  helpText: 'If a check did not work, you can ask a person to review it. A reviewer sees your first name and the kind of check, never your documents.',
  appealNote: 'Anything you want the reviewer to know (optional)',
  appeal: 'Ask for a review',
  appealOpen: 'A review is open. You will see the result here.',
  withdrawAll: 'Withdraw consent and remove my badge',
  withdrawAllConfirm: 'This ends all checks and removes your badge. Continue?',
  badgeText: 'Vieques residency checked',
  identityBadgeText: 'Identity checked',
  badgeWhat: 'What was checked',
  badgeWhatText: 'A current residency check was completed.',
  badgeNot: 'What this does not mean',
  badgeNotText: 'It is not a guarantee of safety or honesty. Always inspect the item and agree the meeting place yourself.',
  testChecks: 'Test controls (local only)',
  testPass: 'Simulate pass',
  testFail: 'Simulate fail',
  testMore: 'Simulate needs more',
  testElsewhere: 'Simulate ID address elsewhere',
  reviewTitle: 'Residency reviews',
  reviewEmpty: 'Nothing waiting.',
  reviewVerify: 'Mark residency checked',
  reviewMore: 'Ask for more',
  reviewDecline: 'Decline',
  reviewAppeal: 'Appeal',
  reviewManual: 'Other evidence',
  reviewId: 'ID address',
  error: 'Something went wrong. Try again.',
  loading: 'Loading',
  back: 'Back to the marketplace',
} as const
type Key = keyof typeof EN

const ES: Record<Key, string> = {
  title: 'Verificación de residencia en Vieques',
  optional: 'Opcional',
  lede: 'La verificación es opcional. Puedes explorar, comprar y vender sin ella. Un residente verificado recibe una insignia y un pequeño impulso transparente en el orden de los anuncios.',
  notAGuarantee: 'Una insignia muestra qué se revisó y cuándo. No promete que alguien sea seguro, honesto o que el intercambio salga bien.',
  testMode: 'Modo de prueba: no se recogen documentos ni fotos reales y no se envía nada a un proveedor.',
  unavailableTitle: 'Todavía no está disponible',
  unavailableText: 'Las verificaciones de residencia no están activadas. No se recoge nada hasta que lo estén.',
  consentTitle: 'Antes de empezar',
  willCheck: 'Qué se va a revisar',
  checkIdentity: 'Identidad: un proveedor especializado revisa una identificación con foto y un selfie en vivo. Es independiente de la residencia.',
  checkResidency: 'Residencia: evidencia de que vives actualmente en Vieques. Una identificación con dirección en Vieques ayuda, o puedes usar otra evidencia que revisa una persona.',
  provider: 'Quién maneja tus documentos',
  providerText: 'Un proveedor especializado recoge la identificación y el selfie en su propia página. Nunca pasan por nuestro almacenamiento.',
  weKeep: 'Qué guardamos',
  weKeepText: 'Solo una referencia, qué revisiones hiciste, el resultado, las fechas y la versión de este consentimiento. No guardamos imágenes de documentos, datos faciales, números de identificación ni tu dirección.',
  publicTitle: 'Qué pueden ver los demás',
  publicText: 'Solo la insignia y la fecha de la revisión. Nunca tu nombre legal, dirección, número de identificación, documentos ni selfie.',
  deletionTitle: 'Retirarte',
  deletionText: 'Puedes retirarte cuando quieras. Tu insignia desaparece y le pedimos al proveedor que borre lo que tiene. Lo que un proveedor deba conservar por ley lo explica él y nosotros no podemos eliminarlo.',
  agree: 'Entiendo y estoy de acuerdo',
  agreeAndContinue: 'Aceptar y continuar',
  identityTitle: 'Verificación de identidad',
  identityText: 'Una identificación con foto y un selfie rápido en vivo, en la página del proveedor.',
  residencyTitle: 'Verificación de residencia',
  residencyText: 'Muestra que vives actualmente en Vieques.',
  methodTitle: '¿Cómo prefieres demostrarlo?',
  methodId: 'Mi identificación muestra una dirección en Vieques',
  methodIdHint: 'Primero necesita la verificación de identidad.',
  methodManual: 'La dirección de mi identificación está desactualizada',
  methodManualHint: 'Comparte otra evidencia. Una persona la revisa.',
  start: 'Empezar',
  retry: 'Intentar de nuevo',
  respond: 'Ya envié más',
  withdraw: 'Retirar',
  statusLabel: 'Estado',
  notStarted: 'Sin empezar',
  pending: 'En proceso',
  needs_info: 'Falta información',
  verified: 'Verificada',
  unsuccessful: 'No se pudo completar',
  expired: 'Vencida',
  withdrawn: 'Retirada',
  pendingHelp: 'Termina los pasos en la página del proveedor. Lo actualizaremos aquí cuando termine.',
  needsInfoHelp: 'Necesitamos un poco más para terminar esta verificación.',
  unsuccessfulHelp: 'No pudimos completar esta verificación. Puedes intentarlo otra vez o pedir que una persona la revise.',
  expiredHelp: 'Esta verificación venció. Empieza otra vez para mantener tu insignia.',
  withdrawnHelp: 'Retiraste esta verificación. Puedes empezar una nueva cuando quieras.',
  address_not_vieques: 'La dirección de tu identificación no es de Vieques. Si vives en Vieques, elige "La dirección de mi identificación está desactualizada".',
  address_not_current: 'Comparte algo que muestre dónde vives ahora.',
  evidence_unclear: 'La evidencia era difícil de leer. Envíala de nuevo.',
  more_evidence: 'Comparte una evidencia más.',
  conflicting_result: 'Recibimos un resultado en conflicto. Una persona lo revisará.',
  checkedOn: 'Verificada el',
  validUntil: 'Válida hasta',
  helpTitle: 'Ayuda y apelación',
  helpText: 'Si una verificación no funcionó, puedes pedir que una persona la revise. Quien revisa ve tu primer nombre y el tipo de verificación, nunca tus documentos.',
  appealNote: 'Algo que quieras que sepa quien revisa (opcional)',
  appeal: 'Pedir una revisión',
  appealOpen: 'Hay una revisión abierta. Verás el resultado aquí.',
  withdrawAll: 'Retirar mi consentimiento y quitar mi insignia',
  withdrawAllConfirm: 'Esto termina todas las verificaciones y quita tu insignia. ¿Continuar?',
  badgeText: 'Residencia en Vieques verificada',
  identityBadgeText: 'Identidad verificada',
  badgeWhat: 'Qué se revisó',
  badgeWhatText: 'Se completó una verificación vigente de residencia.',
  badgeNot: 'Lo que no significa',
  badgeNotText: 'No es garantía de seguridad ni de honestidad. Revisa siempre el artículo y acuerda tú el lugar de encuentro.',
  testChecks: 'Controles de prueba (solo local)',
  testPass: 'Simular éxito',
  testFail: 'Simular fallo',
  testMore: 'Simular que falta información',
  testElsewhere: 'Simular dirección fuera de Vieques',
  reviewTitle: 'Revisiones de residencia',
  reviewEmpty: 'No hay nada pendiente.',
  reviewVerify: 'Marcar residencia verificada',
  reviewMore: 'Pedir más',
  reviewDecline: 'Rechazar',
  reviewAppeal: 'Apelación',
  reviewManual: 'Otra evidencia',
  reviewId: 'Dirección de la identificación',
  error: 'Algo salió mal. Inténtalo de nuevo.',
  loading: 'Cargando',
  back: 'Volver al mercado',
}

export type VerificationKey = Key
export function vt(key: Key): string {
  return (getLocale() === 'es-PR' ? ES : EN)[key]
}
/** For codes that arrive from the server and might not be a known key. */
export function vtCode(code: string | null | undefined): string | null {
  return code && code in EN ? vt(code as Key) : null
}
export const VERIFICATION_STRINGS = { en: EN, es: ES }
